// Regra de DRE por pagamento (espelha o enum TipoDrePagamento do Backend).
// Mantém os mesmos valores numéricos para evitar números mágicos espalhados pelos componentes.
export const TIPO_DRE_PAGAMENTO = Object.freeze({
  TITULOS_POR_DATA_PAGAMENTO: 1, // Títulos — valor da conta (PADRÃO/fallback)
  BAIXAS_POR_VALOR_RECEBIDO: 2, // Baixas — valor recebido
  BAIXAS_POR_VALOR_DA_CONTA: 3, // Baixas — Valor da Conta
});

// Padrão e fallback quando o backend não retorna valor.
export const TIPO_DRE_PAGAMENTO_DEFAULT = TIPO_DRE_PAGAMENTO.TITULOS_POR_DATA_PAGAMENTO;

// Opções exibidas no select "Regra DRE por pagamento".
export const TIPO_DRE_PAGAMENTO_OPTIONS = [
  { value: TIPO_DRE_PAGAMENTO.TITULOS_POR_DATA_PAGAMENTO, label: "Títulos — valor da conta" },
  { value: TIPO_DRE_PAGAMENTO.BAIXAS_POR_VALOR_RECEBIDO, label: "Baixas — valor recebido" },
  { value: TIPO_DRE_PAGAMENTO.BAIXAS_POR_VALOR_DA_CONTA, label: "Baixas — Valor da Conta" },
];

// Explicação dinâmica apresentada abaixo do select.
// Texto curto, em linguagem simples, sem termos técnicos.
const TIPO_DRE_PAGAMENTO_DESCRICOES = {
  [TIPO_DRE_PAGAMENTO.TITULOS_POR_DATA_PAGAMENTO]:
    "Usa o valor cheio da conta, na data em que foi paga. Desconto, juros e multa aparecem em linhas separadas no DRE.",
  [TIPO_DRE_PAGAMENTO.BAIXAS_POR_VALOR_RECEBIDO]:
    "Usa só o valor que realmente entrou ou saiu do caixa. Não mostra desconto, juros e multa em linhas separadas.",
  [TIPO_DRE_PAGAMENTO.BAIXAS_POR_VALOR_DA_CONTA]:
    "Igual ao primeiro (valor cheio + desconto/juros/multa separados), mas conta cada pagamento. Melhor quando a conta é paga em partes.",
};

export function getTipoDrePagamentoDescricao(value) {
  return (
    TIPO_DRE_PAGAMENTO_DESCRICOES[value] ??
    TIPO_DRE_PAGAMENTO_DESCRICOES[TIPO_DRE_PAGAMENTO_DEFAULT]
  );
}

// Exemplo concreto de como o DRE fica em cada regra. Mesmo cenário nos três tipos,
// para o usuário comparar: uma venda de R$ 1.000 recebida por R$ 950 (R$ 50 de desconto).
// Exibido no ícone (i) ao lado da explicação.
const TIPO_DRE_PAGAMENTO_EXEMPLOS = {
  [TIPO_DRE_PAGAMENTO.TITULOS_POR_DATA_PAGAMENTO]: {
    cenario:
      "Exemplo: uma venda de R$ 1.000 foi recebida por R$ 950 (R$ 50 de desconto).",
    linhas: [
      { label: "Receita de Vendas", valor: "R$ 1.000" },
      { label: "(–) Descontos", valor: "– R$ 50" },
      { label: "= Resultado", valor: "R$ 950", total: true },
    ],
    nota: "Mostra o valor cheio e o desconto em linhas separadas.",
  },
  [TIPO_DRE_PAGAMENTO.BAIXAS_POR_VALOR_RECEBIDO]: {
    cenario:
      "Exemplo: uma venda de R$ 1.000 foi recebida por R$ 950 (R$ 50 de desconto).",
    linhas: [{ label: "Receita de Vendas", valor: "R$ 950", total: true }],
    nota: "Mostra só o que entrou. Não cria a linha de Descontos.",
  },
  [TIPO_DRE_PAGAMENTO.BAIXAS_POR_VALOR_DA_CONTA]: {
    cenario:
      "Exemplo: uma venda de R$ 1.000 foi recebida por R$ 950 (R$ 50 de desconto).",
    linhas: [
      { label: "Receita de Vendas", valor: "R$ 1.000" },
      { label: "(–) Descontos", valor: "– R$ 50" },
      { label: "= Resultado", valor: "R$ 950", total: true },
    ],
    nota: "Igual ao primeiro, mas somando cada pagamento (bom para parcelas).",
  },
};

export function getTipoDrePagamentoExemplo(value) {
  return (
    TIPO_DRE_PAGAMENTO_EXEMPLOS[value] ??
    TIPO_DRE_PAGAMENTO_EXEMPLOS[TIPO_DRE_PAGAMENTO_DEFAULT]
  );
}

// Normaliza o valor vindo do backend (ou ausente) para uma das três regras válidas.
export function normalizeTipoDrePagamento(value) {
  const n = Number(value);
  return n === TIPO_DRE_PAGAMENTO.TITULOS_POR_DATA_PAGAMENTO ||
    n === TIPO_DRE_PAGAMENTO.BAIXAS_POR_VALOR_RECEBIDO ||
    n === TIPO_DRE_PAGAMENTO.BAIXAS_POR_VALOR_DA_CONTA
    ? n
    : TIPO_DRE_PAGAMENTO_DEFAULT;
}
